import { useCallback, useEffect, useMemo, useState } from 'react';
import { type Factor, Factors as Client, Passkeys } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';
import { Button, Callout, EmptyState, Field, Form, ListRow, useConfirm, Waiting } from '@beyond-js/ui/react';
import { Panel, Problem, useLast, useQuestions, useReauthenticate, useUnread } from '@accounts/ui';

/**
 * Second factors (decision E8): an authenticator app (its key in groups to type, or opened in the app,
 * then its first code), passkeys in this browser, and recovery codes, shown once when the first factor
 * is turned on and again on request. Removing one says what it changes; the last one takes the recovery
 * codes with it.
 *
 * When Accounts does not answer the list, the page's notice says so once (D163) and the list says it in
 * its place, or over the factors kept from the last read, when that was; the ways to add one stay.
 *
 * The authenticator's first code is a step not yet taken until its six digits are typed (D183): Turn on
 * waits quietly, still reachable, and a press puts the caret in the code, which is sent only once complete.
 */
export function Factors() {
	const { text, date, client, renewed, language } = usePresence();
	// The change sent last, which the person may send again when it was not made (D186)
	const last = useLast();
	const factors = useMemo(() => new Client(client), [client]);
	const { confirm } = useConfirm(useQuestions());
	const reauthenticate = useReauthenticate();
	const [state, setState] = useState<{ factors: Factor[]; recovery: number }>();
	// When the list was last read, and why it could not be read since; a failed change is `failure`
	const [at, setAt] = useState<number>();
	const [unread, setUnread] = useState<Refusal>();
	const [failure, setFailure] = useState<unknown>();
	const [notice, setNotice] = useState<string>();
	const [pending, setPending] = useState<{ id: string; secret: string; uri: string }>();
	const [code, setCode] = useState('');
	const [codes, setCodes] = useState<string[]>();
	const [busy, setBusy] = useState(false);

	// Whether Accounts answered, which takes the read out of the page's outage
	const read = useCallback(
		() =>
			Refusal.answered(async () => {
				setState(await factors.of());
				setAt(Date.now());
			}, setUnread),
		[factors]
	);
	const outage = useUnread({ key: 'factors', title: text('factors.title'), unread, run: read });
	useEffect(() => {
		void read();
	}, [read, renewed]);

	/** Runs a change, once more after proving it is them when the service asks */
	/** @param again Whether no action of its own stays in view (a row's menu), so a failure may offer Try again */
	async function run(work: () => Promise<void>, again = false) {
		if (again) last.keep(() => run(work, true));
		else last.clear();
		setBusy(true);
		setFailure(undefined);
		setNotice(undefined);
		try {
			try {
				await work();
			} catch (error) {
				if (!(Refusal.is(error) && error.code === 'REAUTHENTICATION_REQUIRED' && (await reauthenticate()))) throw error;
				await work();
			}
			await read();
		} catch (error) {
			if (!(Refusal.is(error) && error.quiet)) setFailure(error);
			// Nothing came back, so the change may have been made: the factors say what is true now
			if (Refusal.is(error) && error.unknown) await read();
		} finally {
			setBusy(false);
		}
	}

	const start = () => run(async () => setPending(await factors.totp()));
	const finish = () =>
		run(async () => {
			const done = await factors.confirm((pending as { id: string }).id, code.trim());
			setPending(undefined);
			setCode('');
			setNotice(text('factors.done'));
			if (done.codes) setCodes(done.codes);
		});
	const passkey = () =>
		run(async () => {
			const done = await factors.passkey(text('factors.passkey'));
			setNotice(text('factors.done'));
			if (done.codes) setCodes(done.codes);
		});
	/** New recovery codes replace the current ones, so it is asked first with that consequence (D17) */
	async function renew() {
		const accepted = await confirm({ title: text('factors.recovery.renew.title'), message: text('factors.recovery.renew.body'), accept: text('factors.recovery.renew'), cancel: text('action.cancel'), tone: 'danger' });
		if (accepted) await run(async () => setCodes((await factors.codes()).codes));
	}

	async function remove(factor: Factor) {
		const name = factor.label ?? text(`factors.${factor.kind}`);
		const last = (state?.factors.length ?? 0) <= 1;
		const accepted = await confirm({ title: text('factors.remove.title', { name }), message: last ? text('factors.remove.last') : text('factors.remove.body'), accept: text('factors.remove'), cancel: text('action.cancel'), tone: 'danger' });
		if (accepted) await run(async () => void (await factors.remove(factor.id)), true);
	}

	const grouped = pending?.secret.match(/.{1,4}/g)?.join(' ');
	return (
		<Panel id="factors" title={text('factors.title')} updated={outage && state ? (at ?? null) : undefined}>
			{failure ? <Problem error={failure} again={last.kept ? last.again : undefined} check={read} /> : null}
			{unread && !outage ? <Problem error={unread} /> : null}
			{notice ? <Callout tone="success" live title={notice} /> : null}
			{codes ? (
				<Callout tone="warning" live title={text('factors.recovery.shown')}>
					<ul className="mt-2 grid grid-cols-2 gap-1 font-mono text-small text-ink">
						{codes.map(entry => (
							<li key={entry}>{entry}</li>
						))}
					</ul>
					<div className="mt-2 flex gap-2">
						<a className="inline-flex items-center text-small underline" href={`data:text/plain;charset=utf-8,${encodeURIComponent(codes.join('\n'))}`} download="beyond-recovery-codes.txt">
							{text('factors.recovery.download')}
						</a>
						<Button variant="quiet" small onClick={() => setCodes(undefined)}>
							{text('action.close')}
						</Button>
					</div>
				</Callout>
			) : null}

			{/* The list waits in place while the ways to add one stay drawn (LOAD-01), and says so in place when
			    Accounts does not answer it (D163) */}
			{!state && !unread ? <Waiting language={language} /> : null}
			{outage && !state ? <EmptyState kind="outage" labels={EmptyState.labels[language]} /> : null}
			{state && !state.factors.length && !pending ? <p className="mb-3 max-w-reading text-body text-ink-muted">{text('factors.none')}</p> : null}
			{state?.factors.length ? (
				<ul className="bui-rows" data-framed aria-label={text('factors.title')}>
					{state.factors.map(factor => (
						<ListRow
							key={factor.id}
							title={factor.label ?? text(`factors.${factor.kind}`)}
							meta={[factor.label ? text(`factors.${factor.kind}`) : null, text('factors.added', { date: date(factor.created) })]}
							menu={[{ label: text('factors.remove'), tone: 'danger', disabled: busy, onSelect: () => void remove(factor) }]}
						/>
					))}
				</ul>
			) : null}

			{pending ? (
				<Form
					className="mt-3 flex max-w-form flex-col gap-3"
					onSubmit={event => {
						event.preventDefault();
						void finish();
					}}
				>
					<p className="text-body text-ink">{text('factors.secret')}</p>
					<code className="code-box break-all rounded-beyond border border-line bg-sunken font-mono text-body text-ink">{grouped}</code>
					<a className="text-body underline" href={pending.uri}>
						{text('factors.open')}
					</a>
					<Field label={text('factors.code')}>
						<input inputMode="numeric" autoComplete="one-time-code" maxLength={6} required data-missing={code.trim().length === 6 ? undefined : ''} value={code} onChange={event => setCode(event.target.value)} />
					</Field>
					<div className="flex gap-2">
						<Button type="submit" variant="primary" busy={busy}>
							{text('factors.confirm')}
						</Button>
						<Button type="button" variant="quiet" onClick={() => setPending(undefined)}>
							{text('action.cancel')}
						</Button>
					</div>
				</Form>
			) : (
				<div className="mt-3 flex flex-wrap gap-2">
					<Button variant="secondary" busy={busy} onClick={() => void start()}>
						{text('factors.add.totp')}
					</Button>
					{Passkeys.supported ? (
						<Button variant="secondary" disabled={busy} onClick={() => void passkey()}>
							{text('factors.add.passkey')}
						</Button>
					) : null}
				</div>
			)}

			{state?.factors.length ? (
				<div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-3 border-t border-line pt-3">
					<p className="text-small text-ink-muted">{text('factors.recovery', { count: state.recovery })}</p>
					<Button variant="quiet" small disabled={busy} onClick={() => void renew()}>
						{text('factors.recovery.new')}
					</Button>
				</div>
			) : null}
		</Panel>
	);
}
